import { authStorage } from '../auth/reducer.jsx';
import { recordRequest } from '../common/debug/requestLog.js';

// Get base URL from environment variables or default to proxy path.
// `process` does not exist in a browser bundle, so it has to be feature-tested
// rather than read directly: when VITE_API_BASE_URL is unset (no .env present)
// the bare `process.env` lookup threw at module scope, which took down the
// whole app at boot with a blank page and "process is not defined". Same guard
// as common/utils/platform.jsx. The Electron/node build still gets its value.
const processEnv =
  typeof process !== 'undefined' && process.env ? process.env : {};
const API_BASE_URL =
  import.meta.env?.VITE_API_BASE_URL || processEnv.VITE_API_BASE_URL || '/api';

console.log('API Base URL:', API_BASE_URL);

const apiRequest = async (url, method, body = null, options = {}) => {
  // Support both old signature (customHeaders object) and new signature (options with signal)
  const customHeaders = options.signal ? (options.headers || {}) : options;
  const signal = options.signal;

  const headers = {
    'Accept': 'application/json',
    'X-Platform': 'web',
    ...customHeaders,
  };

  if (!(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // Add auth token if available
  const token = authStorage.getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fetchOptions = {
    method,
    headers,
    credentials: 'include',  // Important for session cookies
    mode: 'cors'
  };

  if (body) {
    fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  // Add abort signal if provided
  if (signal) {
    fetchOptions.signal = signal;
  }

  const _startedAt = Date.now();

  try {
    // console.log(`API Request: ${method} ${API_BASE_URL}${url}`, fetchOptions);

    const response = await fetch(`${API_BASE_URL}${url}`, fetchOptions);

    // Log cookies if they exist
    const cookies = response.headers.get('set-cookie');
    if (cookies) {
      console.log('Received cookies:', cookies);
    }

    if (response.status === 204) {
      return {};
    }

    const responseData = await response.text();
    let parsedData;

    try {
      parsedData = JSON.parse(responseData);
    } catch (error) {
      // If the response isn't JSON, use the raw response
      parsedData = responseData;
    }

    // Staging debug panel. recordRequest is a no-op outside staging.
    recordRequest({
      method,
      url,
      status: response.status,
      ok: response.ok,
      ms: Date.now() - _startedAt,
      at: new Date().toISOString(),
      response: responseData.slice(0, 2000),
    });

    // console.log('API Response:', response.status, parsedData);

    if (!response.ok) {
      // Handle auth errors
      if (response.status === 401) {
        authStorage.removeToken();
        authStorage.removeUser();

        // Public paths that shouldn't redirect on 401.
        //
        // `/signup` belongs here for the same reason `/login` does: the app
        // checks auth on load, an anonymous visitor gets a 401 from that
        // check, and without this the redirect fires and the page is
        // unreachable. Which would make every invite link in every email
        // bounce straight to a sign-in form.
        const publicPaths = ['/agreement', '/landing', '/login', '/signup', '/auth'];
        const isPublicPath = publicPaths.some(path => window.location.pathname.startsWith(path));

        // Redirect to login page (but not if already there or on a public path)
        if (!isPublicPath && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }

      let errorMessage;
      if (typeof parsedData === 'object' && parsedData.error) {
        errorMessage = parsedData.error;
      } else if (typeof parsedData === 'string') {
        errorMessage = parsedData;
      } else {
        errorMessage = 'API error';
      }

      // Create an error object with response info (axios-style)
      const error = new Error(errorMessage);
      error.response = {
        status: response.status,
        data: parsedData
      };
      throw error;
    }

    return parsedData;
  } catch (error) {
    console.error('API request failed:', error);
    // A throw that never reached the response block is a transport failure --
    // DNS, timeout, CORS. Those are the ones with no status code and the ones
    // hardest to see on a phone, so record them explicitly.
    if (!error.response) {
      recordRequest({
        method,
        url,
        status: 0,
        ok: false,
        ms: Date.now() - _startedAt,
        at: new Date().toISOString(),
        response: `${error.name}: ${error.message}`,
      });
    }
    throw error;
  }
};

// Export convenience methods for HTTP verbs
export default {
  get: (url, customHeaders = {}) => apiRequest(url, 'GET', null, customHeaders),
  post: (url, body, customHeaders = {}) => apiRequest(url, 'POST', body, customHeaders),
  put: (url, body, customHeaders = {}) => apiRequest(url, 'PUT', body, customHeaders),
  delete: (url, options = {}) => {
    // Support both body data and custom headers for DELETE
    const body = options.data || null;
    const headers = options.headers || {};
    return apiRequest(url, 'DELETE', body, headers);
  },
  patch: (url, body, customHeaders = {}) => apiRequest(url, 'PATCH', body, customHeaders)
};